/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2024.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */


import {ApiVersion} from "@temple-of-syrinx/qqq-frontend-core/lib/controllers/QControllerV1";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import {QWidgetMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QWidgetMetaData";
import {QCriteriaOperator} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QCriteriaOperator";
import {QFilterCriteria} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QFilterCriteria";
import {QQueryFilter} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QQueryFilter";
import {QTableVariant} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableVariant";
import {Alert, Collapse} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import Tooltip from "@mui/material/Tooltip/Tooltip";
import QContext from "QContext";
import colors from "qqq/assets/theme/base/colors";
import {QCancelButton, QSaveButton} from "qqq/components/buttons/DefaultButtons";
import HelpContent, {hasHelpContent} from "qqq/components/misc/HelpContent";
import AdvancedQueryPreview from "qqq/components/query/AdvancedQueryPreview";
import {getCurrentSortIndicator} from "qqq/components/query/BasicAndAdvancedQueryControls";
import Widget, {HeaderLinkButtonComponent} from "qqq/components/widgets/Widget";
import QQueryColumns, {Column} from "qqq/models/query/QQueryColumns";
import RecordQuery from "qqq/pages/records/query/RecordQuery";
import Client from "qqq/utils/qqq/Client";
import FilterUtils from "qqq/utils/qqq/FilterUtils";
import TableUtils from "qqq/utils/qqq/TableUtils";
import usePossibleValueLabels from "qqq/utils/usePossibleValueLabels";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {unborderedButtonSX} from "qqq/components/widgets/misc/RowBuilderWidget";

interface FilterAndColumnsSetupWidgetProps
{
   isEditable: boolean,
   widgetMetaData: QWidgetMetaData,
   widgetData: any,
   recordValues: { [name: string]: any },
   onSaveCallback?: (values: { [name: string]: any }) => void,
   label?: string
   widgetComponentProps?: any;
}

FilterAndColumnsSetupWidget.defaultProps = {
   onSaveCallback: null
};


const qController = Client.getInstance();
const qControllerV1 = Client.getInstanceV1();

/*******************************************************************************
 ** Component for editing the main setup of a report - that is: filter & columns
 *******************************************************************************/
export default function FilterAndColumnsSetupWidget({isEditable: isEditableProp, widgetMetaData, widgetData, recordValues, onSaveCallback, label, widgetComponentProps}: FilterAndColumnsSetupWidgetProps): JSX.Element
{
   const [modalOpen, setModalOpen] = useState(false);
   const [hideColumns] = useState(widgetData?.hideColumns);
   const [hidePreview] = useState(widgetData?.hidePreview);
   const [hideSortBy] = useState(widgetData?.hideSortBy);
   const [isEditable] = useState(widgetData?.overrideIsEditable ?? isEditableProp);
   const [tableMetaData, setTableMetaData] = useState(null as QTableMetaData);

   const [isApiVersioned] = useState(widgetData?.isApiVersioned);
   const [apiVersion, setApiVersion] = useState(null as ApiVersion | null);

   const [filterFieldName] = useState(widgetData?.filterFieldName ?? "queryFilterJson");
   const [columnsFieldName] = useState(widgetData?.columnsFieldName ?? "columnsJson");

   const [alertContent, setAlertContent] = useState(null as string);
   const [warning, setWarning] = useState(null as string);
   const [widgetFailureAlertContent, setWidgetFailureAlertContent] = useState(null as string);

   const omitExposedJoins: string[] = widgetData?.omitExposedJoins ?? [];

   //////////////////////////////////////////////////////////////////////////////////////////////////
   // we'll actually keep 2 copies of the query filter around here -                               //
   // the one in the record (as json) is one that the backend likes (e.g., possible values as ids) //
   // this "frontend" one is one that the frontend can use (possible values as objects w/ labels). //
   //////////////////////////////////////////////////////////////////////////////////////////////////
   const [frontendQueryFilter, setFrontendQueryFilter] = useState(null as QQueryFilter);

   //////////////////////////////////////////////////////////////////////////////////////////
   // and, separately, the (frontend) filter the editor opens on.  usually the same filter //
   // as the one displayed - but when filterDefaultFieldNames are in play and nothing has  //
   // been stored yet, the display stays empty (nothing is stored, so nothing is shown),   //
   // while the editor still opens with the seeded (locked) criteria in place - so that    //
   // saving from it stores them.                                                          //
   //////////////////////////////////////////////////////////////////////////////////////////
   const [editorQueryFilter, setEditorQueryFilter] = useState(null as QQueryFilter);

   /////////////////////////////////////////////////////////////////////////////////////////
   // the inputs (filterPrepKey) that the two filters above were last prepared for.  that //
   // preparation is async, so until it has caught up with the current inputs, the        //
   // editor would open on a stale (or null) editorQueryFilter - with the seeded criteria //
   // missing or out of date, so nothing locked, and no And forced on save.  hence, with  //
   // filterDefaultFieldNames, the edit buttons stay disabled until the two keys match.   //
   /////////////////////////////////////////////////////////////////////////////////////////
   const [filterPreparedForKey, setFilterPreparedForKey] = useState(null as string);
   const latestFilterPrepKeyRef = useRef(null as string);

   const {helpHelpActive} = useContext(QContext);

   const {getDisplayValues} = usePossibleValueLabels({useCase: "filter"});

   const recordQueryRef = useRef();

   /////////////////////////////
   // load values from record //
   /////////////////////////////
   let columns: QQueryColumns = null;
   let usingDefaultEmptyFilter = false;
   const rawFilterValueFromRecord = recordValues[filterFieldName];
   let queryFilter = rawFilterValueFromRecord &&
      ((typeof rawFilterValueFromRecord == "string" ? JSON.parse(rawFilterValueFromRecord) : rawFilterValueFromRecord) as QQueryFilter);
   const defaultFilterFields = widgetData?.filterDefaultFieldNames;

   //////////////////////////////////////////////////////////////////////////////////////////
   // whether a filter is actually stored on the record - judged by its content, not by    //
   // the raw value being non-blank: {"criteria":[]}, an orderBy-only filter, or the       //
   // string "null" all store no filter.  decided HERE, before the seeding below, because  //
   // that seeding writes into queryFilter.criteria (the same array the parsed record      //
   // value holds) - checked afterwards, every seeded filter would look stored.            //
   //////////////////////////////////////////////////////////////////////////////////////////
   const hasStoredFilter = Boolean(queryFilter) && (queryFilter.criteria?.length > 0 || queryFilter.subFilters?.length > 0);

   if (!queryFilter)
   {
      queryFilter = new QQueryFilter();
      if (defaultFilterFields?.length == 0)
      {
         usingDefaultEmptyFilter = true;
      }
   }
   else
   {
      queryFilter = Object.assign(new QQueryFilter(), queryFilter);
   }


   /***************************************************************************
    * for a default-filter (criteria) - decide if the field name to use for
    * getting values from the record should be the same name as used in the
    * filter being built - or - if a replacement name is set in
    * widgetData.filterDefaultFieldNameSourceFieldNames - then use that name.
    ***************************************************************************/
   function getRecordFieldNameForDefaultFilter(fieldName: string)
   {
      let recordFieldName = fieldName;
      if (widgetData.filterDefaultFieldNameSourceFieldNames?.[fieldName])
      {
         recordFieldName = widgetData.filterDefaultFieldNameSourceFieldNames[fieldName];
      }
      return recordFieldName;
   }


   //////////////////////////////////////////////////////////////////////////////////////////////////////
   // if there are default fields from which a query should be seeded, add/update the filter with them //
   //////////////////////////////////////////////////////////////////////////////////////////////////////
   if (defaultFilterFields?.length > 0)
   {
      defaultFilterFields.forEach((fieldName: string) =>
      {
         //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
         // if a value for the default field exists, make sure a criteria exist for it, with that value, is in the query //
         //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
         let recordFieldName = getRecordFieldNameForDefaultFilter(fieldName);
         if (recordValues[recordFieldName])
         {
            const newCriteria = new QFilterCriteria(fieldName, QCriteriaOperator.EQUALS, [recordValues[recordFieldName]])
            let replacedCriteria = false;
            for (let i = 0; i < (queryFilter.criteria ?? []).length; i++)
            {
               if (queryFilter.criteria[i].fieldName == fieldName)
               {
                  queryFilter.criteria[i] = newCriteria;
                  replacedCriteria = true;
               }
            }

            if (!replacedCriteria)
            {
               queryFilter.addCriteria(newCriteria);
            }
         }
         else
         {
            /////////////////////////////////////////////////////////////////////////////////////////////////////
            // else if there is no value for the field, then make sure there's no criteria for it in our query //
            /////////////////////////////////////////////////////////////////////////////////////////////////////
            queryFilter.criteria = queryFilter.criteria?.filter(c => c.fieldName != fieldName);
         }
      });

      /////////////////////////////////////////////////////////////////////////////////////////
      // move the seeded criteria to the front of the filter, in defaultFilterFields order.  //
      // they are locked further down, and locking is defined as the LEADING criteria of the //
      // filter - but the loop above appends, and a filter stored by an older build has them //
      // wherever they happened to land, so their position cannot be assumed.                //
      /////////////////////////////////////////////////////////////////////////////////////////
      const existingCriteria = queryFilter.criteria;
      if (existingCriteria?.length > 0)
      {
         queryFilter.criteria = [
            ...defaultFilterFields
               .map((fieldName: string) => existingCriteria.find(c => c.fieldName == fieldName))
               .filter((criteria: QFilterCriteria) => criteria),
            ...existingCriteria.filter(c => !defaultFilterFields.includes(c.fieldName))
         ];
      }
   }

   /////////////////////////////////////////////////////////////////////////////////////////
   // the seeding above builds the filter the EDITOR opens on.  it is only DISPLAYED when //
   // a filter is actually stored on the record - with no stored filter, showing seeded   //
   // criteria would present a filter that does not exist (and that, if the editor is     //
   // cancelled, never will).  widgets without default filter fields are unaffected.      //
   /////////////////////////////////////////////////////////////////////////////////////////
   const displayEmptyUntilFilterIsStored = defaultFilterFields?.length > 0 && !hasStoredFilter;

   if (recordValues[columnsFieldName])
   {
      columns = QQueryColumns.buildFromJSON(recordValues[columnsFieldName]);
   }

   ////////////////////////////////////////////////////////////////////////////////////////
   // key for the filter preparation below - built ONLY from the values that preparation //
   // depends on (the filter & columns values, the table, and the record values that     //
   // seed default-filter criteria) - so that typing in some unrelated field of the      //
   // record does not invalidate the prepared filter (and disable the edit buttons).     //
   ////////////////////////////////////////////////////////////////////////////////////////
   const filterPrepKey = JSON.stringify([
      recordValues[filterFieldName] ?? null,
      recordValues[columnsFieldName] ?? null,
      tableMetaData?.name ?? null,
      (defaultFilterFields ?? []).map((fieldName: string) => recordValues[getRecordFieldNameForDefaultFilter(fieldName)] ?? null),
   ]);
   const filterIsPrepared = !(defaultFilterFields?.length > 0) || filterPreparedForKey == filterPrepKey;

   //////////////////////////////////////////////////////////////////////
   // load tableMetaData initially, and if/when selected table changes //
   //////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      ////////////////////////////////////////////////////////////////////////////////////////
      // if a default table name specified, use it, otherwise use it from the record values //
      ////////////////////////////////////////////////////////////////////////////////////////
      let tableName = widgetData?.tableName;
      if (!tableName && recordValues["tableName"] && (tableMetaData == null || tableMetaData.name != recordValues["tableName"]))
      {
         tableName = recordValues["tableName"];
      }

      let version: ApiVersion | null = null;
      if (isApiVersioned)
      {
         let apiName = widgetData?.apiName;
         let apiPath = widgetData?.apiPath;
         let apiVersion = widgetData?.apiVersion;

         if (!apiName && recordValues["apiName"])
         {
            apiName = recordValues["apiName"];
         }

         if (!apiPath && recordValues["apiPath"])
         {
            apiPath = recordValues["apiPath"];
         }

         if (!apiVersion && recordValues["apiVersion"])
         {
            apiVersion = recordValues["apiVersion"];
         }

         if (!apiName || !apiPath || !apiVersion)
         {
            console.log("API Name/Path/Version not set, but widget isApiVersioned, so cannot load table meta data...");
            return;
         }

         version = {name: apiName, path: apiPath, version: apiVersion};
         setApiVersion(version);
      }

      //////////////////////////////////////////////////////////////////////////////////////////////////
      // if we have a tableName, and we've either never loaded tableMetaData, or we had tableMetaData //
      // for a different table - then we should load table meta data for the current table.           //
      //////////////////////////////////////////////////////////////////////////////////////////////////
      if (tableName && (!tableMetaData || tableMetaData.name != tableName))
      {
         (async () =>
         {
            try
            {
               const tableMetaData = await qControllerV1.loadTableMetaData(tableName, version);
               setTableMetaData(tableMetaData);
            }
            catch (e)
            {
               console.log(e);
               //@ts-ignore e.message
               setWidgetFailureAlertContent("Error preparing filter widget: " + (e.message ?? "Details not available."));
            }
         })();
      }

      ////////////////////////////////////////////////////////////////////////////////////////////
      // if we have tableMetaData, make sure the query is good for the frontend to use          //
      // this may include removing criteria for non-existing fields, and cleansing value object //
      ////////////////////////////////////////////////////////////////////////////////////////////
      if (tableMetaData)
      {
         ///////////////////////////////////////////////////////////////////////////////////
         // a preparation that finishes after a newer one has started is discarded - else //
         // it would overwrite the newer filters with stale ones (and a stale key).       //
         ///////////////////////////////////////////////////////////////////////////////////
         const preparingForKey = filterPrepKey;
         latestFilterPrepKeyRef.current = preparingForKey;
         (async () =>
         {
            try
            {
               const queryFilterForFrontend = Object.assign({}, queryFilter);

               let warnings: string[] = [];
               for (let i = 0; i < queryFilterForFrontend?.criteria?.length; i++)
               {
                  const criteria = queryFilter.criteria[i];
                  let [field, fieldTable] = TableUtils.getFieldAndTable(tableMetaData, criteria.fieldName);
                  if(!field)
                  {
                     warnings.push("Removing non-existing filter field: " + criteria.fieldName);
                     queryFilterForFrontend.criteria.splice(i, 1);
                     i--;
                  }
               }

               await FilterUtils.cleanupValuesInFilerFromQueryString(qController, tableMetaData, queryFilterForFrontend, getDisplayValues);
               if (latestFilterPrepKeyRef.current != preparingForKey)
               {
                  return;
               }

               setEditorQueryFilter(queryFilterForFrontend);
               setFrontendQueryFilter(displayEmptyUntilFilterIsStored ? null : queryFilterForFrontend);
               setFilterPreparedForKey(preparingForKey);

               setWarning(warnings.join("; "));
            }
            catch (e)
            {
               console.warn(e);
               //@ts-ignore e.message
               setWidgetFailureAlertContent("Error preparing filter widget: " + (e.message ?? "Details not available."));
            }
         })();
      }
   }, [filterPrepKey, tableMetaData, recordValues["tableName"], recordValues["apiName"], recordValues["apiPath"], recordValues["apiVersion"]]);


   /*******************************************************************************
    **
    *******************************************************************************/
   function openEditor()
   {
      let missingRequiredFields = [] as string[];
      widgetData?.filterDefaultFieldNames?.forEach((fieldName: string) =>
      {
         let recordFieldName = getRecordFieldNameForDefaultFilter(fieldName);
         if (!recordValues[recordFieldName])
         {
            const tableField = tableMetaData.fields.get(recordFieldName);
            missingRequiredFields.push(tableField?.label ?? recordFieldName);
         }
      });

      ////////////////////////////////////////////////////////////////////
      // display an alert and return if any required fields are missing //
      ////////////////////////////////////////////////////////////////////
      if (missingRequiredFields.length > 0)
      {
         setAlertContent("The following fields must first be selected to edit the filter: '" + missingRequiredFields.join(", ") + "'");
         return;
      }

      if (widgetData?.tableName || recordValues["tableName"])
      {
         setAlertContent(null);
         setModalOpen(true);
      }
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   function saveClicked()
   {
      if (!onSaveCallback)
      {
         console.log("onSaveCallback was not defined");
         return;
      }

      // @ts-ignore possibly 'undefined'.
      const view = recordQueryRef?.current?.getCurrentView();

      view.queryColumns.sortColumnsFixingPinPositions();

      //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
      // keep the query filter that came from the recordQuery screen as the front-end version (w/ possible value objects) //
      // but prep a copy of it for the backend, to stringify as json in the record being edited                           //
      //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
      setFrontendQueryFilter(view.queryFilter);
      const filter = FilterUtils.prepQueryFilterForBackend(tableMetaData, view.queryFilter);

      ////////////////////////////////////////////////////////////////////////////////////
      // and the saved filter is now the stored one - so the editor re-opens on it, too //
      ////////////////////////////////////////////////////////////////////////////////////
      setEditorQueryFilter(view.queryFilter);

      //////////////////////////////////////////////////////////////////////////////////////
      // stored as And whenever criteria are locked, because under an Or a locked         //
      // criterion constrains nothing - "clientId = 47 OR name contains x" returns every  //
      // client's matching rows, so an Or does not weaken the lock, it removes it.  The   //
      // query screen already refuses to run such a filter as Or; storing the Or anyway   //
      // would leave the lock a front-end convention, honoured on this screen and ignored //
      // by every server-side consumer of the saved filter - report generation, workflow  //
      // actions - which is the case that actually leaks another client's rows.           //
      //                                                                                  //
      // Written on the backend copy, never on view.queryFilter: that object is the live  //
      // editor state, and rewriting IT is the defect this replaces - it turned merely    //
      // opening the editor into a filter change.  prepQueryFilterForBackend returns a    //
      // fresh QQueryFilter, so this cannot reach the view.                               //
      //                                                                                  //
      // The user is told before this happens - see the locked-criteria caption in        //
      // CustomFilterPanel.  Disclosed and then done is the difference from the old       //
      // behaviour, which did it silently on load.                                        //
      //////////////////////////////////////////////////////////////////////////////////////
      if (lockedCriteriaCount > 0)
      {
         filter.booleanOperator = "AND";
      }

      const rs: { [key: string]: any } = {};
      rs[filterFieldName] = JSON.stringify(filter);
      rs[columnsFieldName] = JSON.stringify(view.queryColumns);
      onSaveCallback(rs);

      closeEditor();
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   function closeEditor(event?: {}, reason?: "backdropClick" | "escapeKeyDown")
   {
      if (reason == "backdropClick" || reason == "escapeKeyDown")
      {
         return;
      }

      setModalOpen(false);
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   function renderColumn(column: Column): JSX.Element
   {
      const [field, table] = FilterUtils.getField(tableMetaData, column.name);

      if (!column || !column.isVisible || column.name == "__check__" || !field)
      {
         return (<React.Fragment />);
      }

      const tableLabelPart = table.name != tableMetaData.name ? table.label + ": " : "";

      return (<Box mr="0.375rem" mb="0.5rem" border={`1px solid ${colors.grayLines.main}`} borderRadius="0.75rem" p="0.25rem 0.75rem">
         {tableLabelPart}{field.label}
      </Box>);
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   function mayShowQuery(): boolean
   {
      if (tableMetaData)
      {
         if (frontendQueryFilter?.criteria?.length > 0 || frontendQueryFilter?.subFilters?.length > 0)
         {
            return (true);
         }
      }

      return (false);
   }

   /*******************************************************************************
    **
    *******************************************************************************/
   function mayShowColumns(): boolean
   {
      if (tableMetaData)
      {
         for (let i = 0; i < columns?.columns?.length; i++)
         {
            if (columns.columns[i].isVisible && columns.columns[i].name != "__check__")
            {
               return (true);
            }
         }
      }

      return (false);
   }

   /////////////////////////////////////////////////////////////////////////////////////////
   // a criterion seeded from a default filter field belongs to the record hosting this   //
   // widget, not to whoever is editing the filter - so it is handed to the editor as     //
   // locked: rendered read-only, not removable, and re-asserted by RecordQuery if        //
   // anything would drop it (Reset All Changes, a saved view, New View...).              //
   //                                                                                     //
   // counted off the prepared filters rather than off defaultFilterFields directly,      //
   // because the effect above drops criteria whose field is not on the table.  a count   //
   // has to describe the array actually being passed down, since RecordQuery reads the   //
   // locked criteria as criteria.slice(0, lockedCriteriaCount) - a count that            //
   // over-reported would lock one of the user's own criteria instead.                    //
   //                                                                                     //
   // so there are two counts, one per filter:                                            //
   // - lockedCriteriaCount, off editorQueryFilter - for the editor's RecordQuery, and    //
   //   for saveClicked's forcing of And.  with no stored filter this is still the count  //
   //   of seeded criteria, since the editor opens with them in place.                    //
   // - displayLockedCriteriaCount, off frontendQueryFilter - for the read-only summary   //
   //   and the preview.  0 while nothing is stored, since then nothing is displayed.     //
   /////////////////////////////////////////////////////////////////////////////////////////
   function countLockedCriteria(filter: QQueryFilter): number
   {
      let count = 0;
      if (defaultFilterFields?.length > 0)
      {
         const criteria = filter?.criteria ?? [];
         while (count < criteria.length && defaultFilterFields.includes(criteria[count].fieldName))
         {
            count++;
         }
      }
      return (count);
   }

   const lockedCriteriaCount = countLockedCriteria(editorQueryFilter);
   const displayLockedCriteriaCount = countLockedCriteria(frontendQueryFilter);

   const helpRoles = isEditable ? [recordValues["id"] ? "EDIT_SCREEN" : "INSERT_SCREEN", "WRITE_SCREENS", "ALL_SCREENS"] : ["VIEW_SCREEN", "READ_SCREENS", "ALL_SCREENS"];

   /*******************************************************************************
    **
    *******************************************************************************/
   function showHelp(slot: string)
   {
      return (helpHelpActive || hasHelpContent(widgetMetaData?.helpContent?.get(slot), helpRoles));
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   function getHelpContent(slot: string)
   {
      const key = `widget:${widgetMetaData.name};slot:${slot}`;
      return <HelpContent helpContents={widgetMetaData?.helpContent?.get(slot)} roles={helpRoles} helpContentKey={key} />;
   }

   ///////////////////////////////////////////////////////////////////////////////
   // Derive a forced tableVariant for the embedded RecordQuery when widgetData //
   // declares variantSourceFieldName + variantTypeKey AND the parent record's  //
   // form has a value for that field.  Read directly from recordValues so it   //
   // tracks unsaved form-state edits (e.g. user picked connection on a new     //
   // record).  When the source field is variant-aware but empty, the action    //
   // buttons disable with a "select <variant> first" tooltip — mirroring the   //
   // existing "select tableName first" gate.                                   //
   //                                                                           //
   // If variantSourcePossibleValueSourceName is also set, asynchronously       //
   // resolve the id to its display label so the variant indicator inside       //
   // the modal reads "Connection: <connection name>" instead of an id.         //
   ///////////////////////////////////////////////////////////////////////////////
   const variantSourceFieldName    = widgetData?.variantSourceFieldName;
   const variantTypeKey            = widgetData?.variantTypeKey;
   const variantSourcePvsName      = widgetData?.variantSourcePossibleValueSourceName;
   const variantRequired           = !!(variantSourceFieldName && variantTypeKey);
   const variantSourceValue        = variantRequired ? recordValues?.[variantSourceFieldName] : null;
   const [variantDisplayName, setVariantDisplayName] = useState<string | null>(null);

   useEffect(() =>
   {
      if (!variantSourcePvsName || variantSourceValue == null || variantSourceValue === "")
      {
         setVariantDisplayName(null);
         return;
      }
      let cancelled = false;
      (async () =>
      {
         try
         {
            const {results} = await qController.possibleValues({
               fieldNameOrPossibleValueSourceName: variantSourcePvsName,
               ids: [variantSourceValue],
               useCase: "form",
            } as any);
            if (cancelled)
            {
               return;
            }
            const label = results?.[0]?.label;
            setVariantDisplayName(label ?? null);
         }
         catch (e)
         {
            console.warn("Failed to resolve variant display name for forced tableVariant", e);
         }
      })();
      return () =>
      {
         cancelled = true;
      };
   }, [variantSourcePvsName, variantSourceValue]);

   const forcedTableVariant = (variantRequired && variantSourceValue != null && variantSourceValue !== "")
      ? {id: variantSourceValue, type: variantTypeKey, name: variantDisplayName ?? String(variantSourceValue)} as QTableVariant
      : null;

   const variantSourceFieldLabel = variantRequired
      ? (tableMetaData?.variantTableLabel ?? variantSourceFieldName)
      : null;

   /////////////////////////////////////////////////
   // add link to widget header for opening modal //
   /////////////////////////////////////////////////
   const needsVariantSelection = variantRequired && !forcedTableVariant;
   let selectTableFirstTooltipTitle: string | null = null;
   if (!tableMetaData)
   {
      selectTableFirstTooltipTitle = `You must select a table${isApiVersioned ? " and API details" : ""} before you can set up your filters${hideColumns ? "" : " and columns"}`;
   }
   else if (needsVariantSelection)
   {
      selectTableFirstTooltipTitle = `You must select a ${variantSourceFieldLabel} before you can set up your filters${hideColumns ? "" : " and columns"}`;
   }
   else if (!filterIsPrepared)
   {
      selectTableFirstTooltipTitle = "Preparing the filter...";
   }
   const actionButtonsDisabled = tableMetaData == null || needsVariantSelection || !filterIsPrepared;
   const labelAdditionalElementsRight: JSX.Element[] = [];
   if (isEditable)
   {
      let buttonLabel = "Edit Filters and Columns";
      if (hideColumns)
      {
         buttonLabel = "Edit Filters";
      }

      if(widgetData.editButtonLabel)
      {
         buttonLabel = widgetData.editButtonLabel;
      }

      labelAdditionalElementsRight.push(<HeaderLinkButtonComponent key="filterAndColumnsHeader" label={buttonLabel} onClickCallback={openEditor} disabled={actionButtonsDisabled} disabledTooltip={selectTableFirstTooltipTitle} className="editFiltersButton" />);
   }

   if (widgetFailureAlertContent)
   {
      return (<Widget widgetMetaData={widgetMetaData}>
         <Alert severity="error" sx={{mt: 1.5, mb: 0.5}}>{widgetFailureAlertContent}</Alert>
      </Widget>);
   }

   let omitLabel = false;
   if(widgetComponentProps?.omitLabel)
   {
      omitLabel = true;
   }

   return (<Widget widgetMetaData={widgetMetaData} labelAdditionalElementsRight={labelAdditionalElementsRight} {...(widgetComponentProps ?? {})}>
      <React.Fragment>
         {
            showHelp("sectionSubhead") &&
            <Box color={colors.gray.main} pb={"0.5rem"} fontSize={"0.875rem"}>
               {getHelpContent("sectionSubhead")}
            </Box>
         }
         <Collapse in={Boolean(alertContent)}>
            <Alert severity="error" sx={{mt: 1.5, mb: 0.5}} onClose={() => setAlertContent(null)}>{alertContent}</Alert>
         </Collapse>
         <Collapse in={Boolean(warning)}>
            <Alert severity="warning" sx={{mt: 1.5, mb: 0.5}} onClose={() => setWarning(null)}>{warning}</Alert>
         </Collapse>
         <Box pt="0.5rem" className="filterAndColumnsSetupFilter" data-filter-prepared={tableMetaData && filterIsPrepared ? "true" : "false"}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
               {!omitLabel && <h5>{label ?? widgetData.label ?? widgetMetaData.label ?? "Query Filter"}</h5>}
               {!hideSortBy && <Box fontSize="0.75rem" fontWeight="700" ml="auto">{mayShowQuery() && getCurrentSortIndicator(frontendQueryFilter, tableMetaData, null)}</Box>}
            </Box>
            {
               mayShowQuery() &&
               ///////////////////////////////////////////////////////////////////////////////////
               // lockedCriteriaCount is passed even though nothing here is editable, because   //
               // this line renders the And/Or between conditions - and without it this         //
               // read-only summary showed "Or" while the editor's preview showed "And" for the //
               // very same filter.  And is what the queries beneath it actually run, and what  //
               // the next save will store.                                                     //
               //                                                                               //
               // Note the stored record may still SAY Or until that next save - normalisation  //
               // happens on save, not on load, deliberately: rewriting a stored filter merely  //
               // because someone opened the screen is the defect this replaced.  So a filter   //
               // authored elsewhere reads as And here while the record still holds Or, and any //
               // server-side consumer of that record runs it as Or until it is saved once.     //
               ///////////////////////////////////////////////////////////////////////////////////
               <AdvancedQueryPreview tableMetaData={tableMetaData} queryFilter={frontendQueryFilter} isEditable={false} isQueryTooComplex={frontendQueryFilter.subFilters?.length > 0} removeCriteriaByIndexCallback={null} lockedCriteriaCount={displayLockedCriteriaCount} />
            }
            {
               !mayShowQuery() &&
               <Box width="100%" sx={{fontSize: "1rem", background: "#FFFFFF"}} minHeight={"2.5rem"} p={"0.5rem"} pb={"0.125rem"} borderRadius="0.75rem" border={`1px solid ${colors.grayLines.main}`}>
                  {
                     isEditable &&
                     <Tooltip title={selectTableFirstTooltipTitle}>
                        <span><Button disabled={actionButtonsDisabled} sx={{mb: "0.125rem", ...unborderedButtonSX}} onClick={openEditor}>+ Add Filters</Button></span>
                     </Tooltip>
                  }
                  {
                     !isEditable && <Box color={colors.gray.main}>No filters are configured.</Box>
                  }
               </Box>
            }
         </Box>
         {!hideColumns && (
            <Box pt="1rem">
               <h5>Columns</h5>
               <Box display="flex" flexWrap="wrap" fontSize="1rem">
                  {
                     mayShowColumns() && columns &&
                     columns.columns.map((column, i) => <React.Fragment key={`column-${i}`}>{renderColumn(column)}</React.Fragment>)
                  }
                  {
                     !mayShowColumns() &&
                     <Box width="100%" sx={{fontSize: "1rem", background: "#FFFFFF"}} minHeight={"2.375rem"} p={"0.5rem"} pb={"0.125rem"}>
                        {
                           isEditable &&
                           <Tooltip title={selectTableFirstTooltipTitle}>
                              <span><Button disabled={actionButtonsDisabled} sx={unborderedButtonSX} onClick={openEditor}>+ Add Columns</Button></span>
                           </Tooltip>
                        }
                        {
                           !isEditable && <Box color={colors.gray.main}>No columns are selected.</Box>
                        }
                     </Box>
                  }
               </Box>
            </Box>
         )}
         {!hidePreview && !isEditable && frontendQueryFilter && tableMetaData && (
            <Box pt="1rem">
               <h5>Preview</h5>
               <RecordQuery
                  allowVariables={widgetData?.allowVariables}
                  ref={recordQueryRef}
                  table={tableMetaData}
                  isPreview={true}
                  usage="reportSetup"
                  isModal={true}
                  initialQueryFilter={frontendQueryFilter}
                  initialColumns={columns}
                  apiVersion={apiVersion}
                  omitExposedJoins={omitExposedJoins}
                  forcedTableVariant={forcedTableVariant}

                  //////////////////////////////////////////////////////////////////////////////////
                  // the preview has to be told about the lock too, or it queries with the Or the //
                  // stored filter still carries while the summary line right above it says And - //
                  // and the rows it lists are the ones the lock exists to exclude.  Under        //
                  // isPreview this only reaches the query-time enforcement; the And/Or control   //
                  // itself is not rendered.                                                      //
                  //////////////////////////////////////////////////////////////////////////////////
                  lockedCriteriaCount={displayLockedCriteriaCount}
               />
            </Box>
         )}
         {
            modalOpen &&
            <Modal open={modalOpen} onClose={(event, reason) => closeEditor(event, reason)}>
               <div>
                  <Box sx={{position: "absolute", overflowY: "auto", maxHeight: "100%", width: "100%"}}>
                     <Card sx={{m: "2rem", p: "2rem"}}>
                        <h3>{widgetData.modalHeader ?? `Edit Filters ${hideColumns ? "" : " and Columns"}`}</h3>
                        {
                           showHelp("modalSubheader") &&
                           <Box color={colors.gray.main} pb={"0.5rem"}>
                              {getHelpContent("modalSubheader")}
                           </Box>
                        }
                        {
                           tableMetaData && <RecordQuery
                              allowVariables={widgetData?.allowVariables}
                              ref={recordQueryRef}
                              table={tableMetaData}
                              usage="reportSetup"
                              isModal={true}
                              initialQueryFilter={usingDefaultEmptyFilter ? null : editorQueryFilter}
                              initialColumns={columns}
                              apiVersion={apiVersion}
                              omitExposedJoins={omitExposedJoins}
                              forcedTableVariant={forcedTableVariant}
                              lockedCriteriaCount={lockedCriteriaCount}
                           />
                        }

                        <Box>
                           <Box display="flex" justifyContent="flex-end">
                              <QCancelButton disabled={false} onClickHandler={closeEditor} />
                              <QSaveButton label="OK" iconName="check" disabled={false} onClickHandler={saveClicked} />
                           </Box>
                        </Box>
                     </Card>
                  </Box>
               </div>
            </Modal>
         }
      </React.Fragment>
   </Widget>);
}
